import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { Icon } from '../components/icons';
import { Avatar, Button, Card, Empty, ErrorNote, ProgressBar, Segmented, SkeletonCard, Stat, cx } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { CHANNEL_LABEL, formatHours, type DashboardOverview } from '../lib/dashboard';
import { formatDuration, scoreText } from '../lib/format';
import { useNow } from '../lib/hooks';

type Window = '7' | '30' | '90';

const FUNNEL_STEPS: Array<{ key: keyof DashboardOverview['funnel']; label: string }> = [
  { key: 'imported', label: 'Imported' },
  { key: 'reached', label: 'Reached' },
  { key: 'interviewed', label: 'Interviewed' },
  { key: 'qualified', label: 'Qualified' },
  { key: 'shortlisted', label: 'Shortlisted' },
];

/** Brand ramp from pink to purple, one stop per funnel step. */
const RAMP = ['#d6217f', '#c4248a', '#a8279a', '#92299f', '#7e2ea6'];

function greeting(now: Date): string {
  const h = now.getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

export function OverviewPage() {
  const { user } = useAuth();
  const [win, setWin] = useState<Window>('7');
  const q = useQuery({
    queryKey: ['dashboard', 'overview', win],
    queryFn: () => api.get<DashboardOverview>(`/api/dashboard/overview?days=${win}`),
    refetchInterval: 15_000,
  });
  const d = q.data;
  const today = new Date();
  const firstName = user?.name.split(/\s+/)[0] ?? 'there';
  const windowLabel = win === '90' ? 'this quarter' : `the last ${win} days`;
  const started = d ? d.channelMix.phone + d.channelMix.video + d.channelMix.web : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">{today.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
          <h1 className="display mt-2 text-[36px] font-medium leading-none text-fg sm:text-[44px]">
            {greeting(today)}, {firstName}
          </h1>
          <p className="mt-3 text-[15px] text-muted-fg">
            {d ? (
              <>
                <strong className="font-semibold text-fg">{d.totals.interviewsCompleted}</strong> interview{d.totals.interviewsCompleted === 1 ? '' : 's'} completed in {windowLabel}
                {d.totals.needsReview > 0 ? (
                  <>
                    , <strong className="font-semibold text-warning">{d.totals.needsReview}</strong> waiting for your review
                  </>
                ) : null}
                {d.liveNow.length > 0 ? (
                  <>
                    , <strong className="font-semibold text-pink">{d.liveNow.length}</strong> live right now
                  </>
                ) : null}
                .
              </>
            ) : (
              'Loading your pipeline…'
            )}
          </p>
        </div>
        <Segmented<Window>
          label="Time window"
          value={win}
          onChange={setWin}
          options={[
            { value: '7', label: '7 days' },
            { value: '30', label: '30 days' },
            { value: '90', label: 'Quarter' },
          ]}
        />
      </div>

      <ErrorNote error={q.error} />
      {q.isLoading && (
        <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <SkeletonCard key={i} lines={2} />
          ))}
        </div>
      )}

      {d && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
            <Stat label="Leads imported" value={d.totals.leads} icon="upload" note={`${started} interview${started === 1 ? '' : 's'} started`} className="fade-up fade-up-1" />
            <Stat
              label="Interviews completed"
              value={d.totals.interviewsCompleted}
              icon="headphones"
              note={
                <span className="flex flex-wrap gap-x-3">
                  <span>Phone {d.channelMix.phone}</span>
                  <span>Video {d.channelMix.video}</span>
                  <span>Web {d.channelMix.web}</span>
                </span>
              }
              className="fade-up fade-up-2"
            />
            <Stat
              label="Qualified"
              value={d.totals.qualified}
              icon="star"
              accent
              note={d.totals.interviewsCompleted ? `${Math.round((d.totals.qualified / Math.max(1, d.totals.interviewsCompleted)) * 100)}% of completed interviews` : 'No verdicts yet'}
              className="fade-up fade-up-3"
            />
            <Stat label="Lead to verdict" value={formatHours(d.totals.avgHoursLeadToVerdict)} icon="clock" note="Average time from import to verdict" className="fade-up fade-up-4" />
          </div>

          <div className="grid gap-4 xl:grid-cols-[1.6fr_1fr]">
            <FunnelCard d={d} />
            <LiveCard live={d.liveNow} />
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <ReviewCard d={d} />
            <JobsCard d={d} />
          </div>
        </>
      )}
    </div>
  );
}

function FunnelCard({ d }: { d: DashboardOverview }) {
  const max = Math.max(1, d.funnel.imported);
  return (
    <Card title="Funnel" description="Where the leads imported in this window are now." actions={<Icon name="filter" className="h-4 w-4 text-faint" />}>
      <ol className="space-y-3.5">
        {FUNNEL_STEPS.map((s, i) => {
          const n = d.funnel[s.key];
          const prev = i === 0 ? null : d.funnel[FUNNEL_STEPS[i - 1]!.key];
          const rate = prev ? Math.round((n / Math.max(1, prev)) * 100) : null;
          const pct = (n / max) * 100;
          return (
            <li key={s.key} className="grid grid-cols-[6.5rem_1fr_4.5rem] items-center gap-3 text-sm">
              <span className="font-medium text-fg-2">{s.label}</span>
              <span className="relative h-8 overflow-hidden rounded-[8px] bg-muted" aria-hidden>
                <span className="absolute inset-y-0 left-0 rounded-[8px] transition-[width] duration-700" style={{ width: `${Math.max(pct, n > 0 ? 2 : 0)}%`, background: RAMP[i] }} />
                {rate !== null && n > 0 && <span className="absolute inset-y-0 left-2 flex items-center font-mono text-[11px] font-medium text-white/90 mix-blend-luminosity">{rate}% of previous</span>}
              </span>
              <span className="text-right font-mono text-sm tabular text-fg">{n}</span>
            </li>
          );
        })}
      </ol>
      <div className="mt-6 grid gap-4 border-t border-border pt-5 text-sm sm:grid-cols-2">
        <div>
          <p className="eyebrow">Biggest drop-off</p>
          <p className="mt-1.5">
            {d.topDropOff ? (
              <>
                <span className="capitalize">{d.topDropOff.from}</span> <Icon name="arrowRight" className="inline h-3.5 w-3.5 text-faint" /> <span className="capitalize">{d.topDropOff.to}</span>:{' '}
                <span className="font-semibold">{d.topDropOff.rate}%</span> <span className="text-muted-fg">({d.topDropOff.lost} lost)</span>
              </>
            ) : (
              <span className="text-muted-fg">Not enough data yet</span>
            )}
          </p>
        </div>
        <div>
          <p className="eyebrow">Top knockout</p>
          <p className="mt-1.5">
            {d.topKnockout ? (
              <>
                {d.topKnockout.message} <span className="font-mono text-xs text-muted-fg">× {d.topKnockout.count}</span>
              </>
            ) : (
              <span className="text-muted-fg">No rule has failed anyone</span>
            )}
          </p>
        </div>
      </div>
    </Card>
  );
}

function ChannelChip({ channel }: { channel: keyof typeof CHANNEL_LABEL }) {
  const colour = channel === 'VIDEO' ? 'bg-[#d6217f]/25 text-[#f7a6cf]' : channel === 'PHONE' ? 'bg-[#7e2ea6]/35 text-[#d9b3f0]' : 'bg-white/10 text-white/80';
  return <span className={cx('rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold tracking-wider', colour)}>{channel}</span>;
}

function LiveCard({ live }: { live: DashboardOverview['liveNow'] }) {
  const now = useNow();
  return (
    <Card
      tone="night"
      title={
        <span className="flex items-center gap-2">
          <span className="live-dot h-2 w-2 rounded-full bg-[#d6217f] shadow-[0_0_10px_#d6217f]" aria-hidden /> Live now
        </span>
      }
      actions={
        <Link to="/live" className="inline-flex items-center gap-1 font-mono text-[11px] text-white/60 transition hover:text-white">
          Live wall <Icon name="arrowUpRight" className="h-3.5 w-3.5" />
        </Link>
      }
      className="flex flex-col"
    >
      {live.length === 0 ? (
        <div className="flex flex-col items-center py-6 text-center">
          <span className="relative mb-4 flex h-12 w-12 items-center justify-center">
            <span className="pulse-ring absolute inset-0 rounded-full bg-[#d6217f]/40" />
            <span className="relative h-3 w-3 rounded-full bg-[#d6217f]" />
          </span>
          <p className="text-sm text-white/70">Nobody is in an interview right now.</p>
          <p className="mt-1 text-xs text-white/45">Calls and web interviews appear here the moment they start.</p>
        </div>
      ) : (
        <ul className="-mx-2 divide-y divide-white/10">
          {live.slice(0, 6).map((i) => (
            <li key={i.id}>
              <Link to={`/candidates/${i.candidate.id}`} className="flex items-center gap-3 rounded-[9px] px-2 py-2.5 transition hover:bg-white/5">
                <Avatar name={i.candidate.name} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{i.candidate.name}</span>
                  <span className="block truncate text-xs text-white/50">{i.candidate.jobTitle}</span>
                </span>
                <ChannelChip channel={i.channel} />
                <span className="w-12 text-right font-mono text-xs tabular text-white/70">{i.status === 'RINGING' ? 'ringing' : i.startedAt ? formatDuration(now - new Date(i.startedAt).getTime()) : '—'}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ReviewCard({ d }: { d: DashboardOverview }) {
  return (
    <Card title="Needs your review" description="Borderline scores and soft rules land here after scoring." actions={<span className="font-mono text-xs text-muted-fg">{d.totals.needsReview} waiting</span>}>
      {d.needsReview.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-fg">Nothing waiting for you.</p>
      ) : (
        <ul className="-mx-2 divide-y divide-border">
          {d.needsReview.map((c) => (
            <li key={c.id}>
              <Link to={`/candidates/${c.id}`} className="group flex items-center gap-3.5 rounded-[9px] px-2 py-2.5 transition hover:bg-surface-2">
                <span className={cx('display w-11 shrink-0 text-center text-[26px] font-medium tabular', c.score !== null ? scoreText(c.score) : 'text-faint')}>{c.score ?? '—'}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-fg group-hover:text-accent">{c.name}</span>
                  <span className="block truncate text-xs text-muted-fg">
                    {c.job.title}
                    {c.reason ? ` · ${c.reason}` : ''}
                  </span>
                </span>
                <Icon name="chevronRight" className="h-4 w-4 text-faint transition group-hover:text-accent" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function JobsCard({ d }: { d: DashboardOverview }) {
  return (
    <Card
      title="Active jobs"
      description="How far each job's leads have come."
      actions={
        <Button size="xs" variant="ghost" to="/jobs" iconRight="arrowRight">
          All jobs
        </Button>
      }
    >
      {d.activeJobs.length === 0 ? (
        <Empty
          icon="briefcase"
          title="No active jobs"
          action={
            <Button variant="primary" size="sm" icon="plus" to="/jobs/new">
              Create a job
            </Button>
          }
        >
          Set a job to Active to start interviewing its leads.
        </Empty>
      ) : (
        <ul className="space-y-4">
          {d.activeJobs.map((j) => {
            const pct = j.total ? Math.round((j.interviewed / j.total) * 100) : 0;
            return (
              <li key={j.id}>
                <Link to={`/jobs/${j.id}`} className="group block">
                  <div className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="truncate font-semibold text-fg group-hover:text-accent">{j.title}</span>
                    <span className="shrink-0 font-mono text-xs text-muted-fg">
                      {j.interviewed}/{j.total} interviewed
                    </span>
                  </div>
                  <p className="mb-2 text-xs text-muted-fg">{j.location}</p>
                  <ProgressBar value={pct} />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
